import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { globalSearch } from "../api";
import { track } from "../utils/analytics";

// Global search box: words, roots, sentences and topics in one grouped,
// keyboard-navigable dropdown. Backed by GET /api/search/ (core.search).
//
// Fast by construction:
//  * debounced (no request per keystroke),
//  * stale responses are dropped (AbortController + request sequence),
//  * results are memoised per query for the session (retyping is instant).

const DEBOUNCE_MS = 180;
const CACHE_MAX = 80;
const RECENT_KEY = "muraja:recentSearches";
const RECENT_MAX = 8;
const MORE_LIMIT = 30;

const ARABIC_RE = /[\u0621-\u064A\u0671-\u06D3]/;
const HARAKAT_RE = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecent(list) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // best-effort
  }
}

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Wrap the matched query in <mark>. Arabic matches ignore ḥarakāt on both
// sides (the backend matched on the stripped form), so we map the stripped
// match back onto the original vocalised string.
export function highlight(text, query) {
  if (!text || !query) return text;
  if (ARABIC_RE.test(query)) {
    const q = query.replace(new RegExp(HARAKAT_RE.source, "g"), "");
    if (!q) return text;
    const map = []; // stripped index -> original index
    let stripped = "";
    for (let i = 0; i < text.length; i++) {
      if (HARAKAT_RE.test(text[i])) continue;
      map.push(i);
      stripped += text[i];
    }
    const at = stripped.indexOf(q);
    if (at < 0) return text;
    const start = map[at];
    let end = map[at + q.length - 1] + 1;
    // Include trailing ḥarakāt of the last matched letter.
    while (end < text.length && HARAKAT_RE.test(text[end])) end++;
    return (
      <>
        {text.slice(0, start)}
        <mark className="gs-mark">{text.slice(start, end)}</mark>
        {text.slice(end)}
      </>
    );
  }
  const re = new RegExp(escapeRe(query.trim()), "i");
  const m = re.exec(text);
  if (!m) return text;
  const start = m.index;
  const end = start + m[0].length;
  return (
    <>
      {text.slice(0, start)}
      <mark className="gs-mark">{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  );
}

const EMPTY = { words: [], roots: [], sentences: [], topics: [], counts: {} };

export default function GlobalSearch({
  isAdmin = false,
  onSelectWord,
  onSelectSentence,
  onSelectTopic,
  onAddWord,
  placeholder = "Search Arabic or English…",
  autoFocus = false,
  className = "",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [recent, setRecent] = useState(loadRecent);
  const [expanded, setExpanded] = useState({}); // group -> true once "more" fetched

  const inputRef = useRef(null);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const cacheRef = useRef(new Map());
  const seqRef = useRef(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const emptyTimerRef = useRef(null);

  const trimmed = query.trim();
  const isArabic = ARABIC_RE.test(trimmed);

  const runSearch = useCallback(async (q) => {
    const key = q;
    const cached = cacheRef.current.get(key);
    if (cached) {
      setResults(cached);
      setLoading(false);
      setError(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await globalSearch(q, { signal: controller.signal });
      if (seq !== seqRef.current) return; // a newer query is in flight
      const cache = cacheRef.current;
      cache.set(key, data);
      if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
      setResults(data);
      setExpanded({});
      const total = data.words.length + data.roots.length + data.sentences.length + data.topics.length;
      if (total === 0 && q.length >= 2) {
        clearTimeout(emptyTimerRef.current);
        emptyTimerRef.current = setTimeout(() => {
          // Only report when the learner actually stopped on this query.
          if (seq === seqRef.current) track("search_empty", { q, script: data.script });
        }, 1200);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      if (seq !== seqRef.current) return;
      console.error(err);
      setError("Search failed");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  // Debounced search on input change.
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!trimmed) {
      seqRef.current++;
      if (abortRef.current) abortRef.current.abort();
      setResults(null);
      setLoading(false);
      setError(null);
      setActive(-1);
      return;
    }
    if (cacheRef.current.has(trimmed)) {
      runSearch(trimmed);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [trimmed, runSearch]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const remember = (q) => {
    if (!q) return;
    const next = [q, ...recent.filter((r) => r !== q)].slice(0, RECENT_MAX);
    setRecent(next);
    saveRecent(next);
  };

  const reset = () => {
    setQuery("");
    setResults(null);
    setActive(-1);
    setOpen(false);
  };

  const pickWord = (w) => { remember(trimmed); reset(); onSelectWord?.(w); };
  const pickSentence = (s) => { remember(trimmed); reset(); onSelectSentence?.(s); };
  const pickTopic = (t) => { remember(trimmed); reset(); onSelectTopic?.(t); };

  const data = results || EMPTY;
  const hasExactWord = data.words.some((w) => w.text === trimmed);
  // Admin "add word" offer: single Arabic token, nothing exact already, and
  // not a root lookup (spaced radicals are not a word).
  const showAddWord = isAdmin && !!trimmed && isArabic && !/\s/.test(trimmed) && !loading && !error
    && !!results && !hasExactWord && data.roots.length === 0;

  // Flat list of keyboard-selectable rows, in render order.
  const rows = useMemo(() => {
    const out = [];
    data.roots.forEach((r) => r.words.forEach((w) => out.push({ kind: "word", item: w, group: "root" })));
    data.words.forEach((w) => out.push({ kind: "word", item: w, group: "words" }));
    data.sentences.forEach((s) => out.push({ kind: "sentence", item: s, group: "sentences" }));
    data.topics.forEach((t) => out.push({ kind: "topic", item: t, group: "topics" }));
    return out;
  }, [data]);

  const activate = (row) => {
    if (!row) return;
    if (row.kind === "word") pickWord(row.item);
    else if (row.kind === "sentence") pickSentence(row.item);
    else pickTopic(row.item);
  };

  useEffect(() => {
    if (active < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-row="${active}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") { reset(); inputRef.current?.blur(); return; }
    if (!rows.length) {
      if (e.key === "Enter" && showAddWord) { e.preventDefault(); onAddWord?.(trimmed); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % rows.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? rows.length - 1 : a - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      activate(rows[active >= 0 ? active : 0]);
    }
  };

  const loadMore = async (group) => {
    try {
      const more = await globalSearch(trimmed, { scope: group, limit: MORE_LIMIT });
      setResults((prev) => ({ ...(prev || EMPTY), [group]: more[group] }));
      setExpanded((x) => ({ ...x, [group]: true }));
    } catch (err) {
      console.error(err);
    }
  };

  let rowIndex = -1;
  const rowProps = (row) => {
    rowIndex += 1;
    const idx = rowIndex;
    return {
      "data-row": idx,
      onMouseEnter: () => setActive(idx),
      onMouseDown: (e) => e.preventDefault(), // keep input focus
      onClick: () => activate(row),
      className: `gs-row ${active === idx ? "gs-row-active" : ""}`,
      role: "option",
      "aria-selected": active === idx,
    };
  };

  const showRecent = open && !trimmed && recent.length > 0;
  const showDropdown = open && (trimmed ? (results || loading || error) : showRecent);
  const noResults = results && rows.length === 0 && !loading;

  const MoreButton = ({ group }) => {
    const count = data.counts?.[group] || 0;
    if (expanded[group] || count <= data[group].length) return null;
    return (
      <button type="button" className="gs-more" onMouseDown={(e) => e.preventDefault()} onClick={() => loadMore(group)}>
        Show all {count}
      </button>
    );
  };

  return (
    <div ref={rootRef} className={`gs-root ${className}`}>
      <div className="surface-glass relative rounded-xl">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10" style={{ color: "var(--parchment-dim)" }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Search words, sentences and topics"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={!!showDropdown}
          aria-controls="gs-listbox"
          autoComplete="off"
          spellCheck={false}
          className="relative w-full pl-9 pr-8 py-2 text-base bg-transparent focus:outline-none gs-input"
          style={{ background: "transparent", border: "none" }}
        />
        {trimmed && (
          <button type="button" className="gs-clear" aria-label="Clear search" onMouseDown={(e) => e.preventDefault()} onClick={() => { reset(); inputRef.current?.focus(); }}>×</button>
        )}
      </div>

      {loading && trimmed && <p className="text-xs mt-1 px-1" style={{ color: "var(--parchment-dim)" }}>Searching…</p>}
      {error && <p className="text-xs mt-1 px-1" style={{ color: "var(--tag-rose)" }}>{error}</p>}

      {showAddWord && (
        <button
          type="button"
          onClick={() => { const t = trimmed; reset(); onAddWord?.(t); }}
          className="dt-ghost-btn w-full rounded-xl border-dashed font-medium mt-2 px-3 py-2 text-xs"
        >
          + Add “{trimmed}” as a new word
        </button>
      )}

      {showDropdown && (
        <div ref={listRef} id="gs-listbox" className="surface mt-2 gs-dropdown scroll-elegant" role="listbox">
          {showRecent && (
            <div className="gs-group">
              <div className="gs-group-head">
                <span>Recent</span>
                <button type="button" className="gs-link" onMouseDown={(e) => e.preventDefault()} onClick={() => { setRecent([]); saveRecent([]); }}>Clear</button>
              </div>
              {recent.map((r) => (
                <button key={r} type="button" className="gs-row" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQuery(r); setOpen(true); }}>
                  <span className="gs-recent-icon" aria-hidden="true">↺</span>
                  <span className={ARABIC_RE.test(r) ? "font-arabic" : ""}>{r}</span>
                </button>
              ))}
            </div>
          )}

          {noResults && !error && (
            <div className="gs-empty">
              No matches for “{trimmed}”.
              {!isArabic && <span className="block mt-1" style={{ color: "var(--parchment-dim)" }}>Try the Arabic spelling or a simpler English word.</span>}
            </div>
          )}

          {data.roots.map((root) => (
            <div key={`root-${root.id}`} className="gs-group">
              <div className="gs-group-head">
                <span>Root <span className="font-arabic text-base" dir="rtl">{root.pattern}</span>{root.gloss ? ` · ${root.gloss}` : ""}</span>
              </div>
              {root.words.map((w) => (
                <button key={`rw-${w.id}`} type="button" {...rowProps({ kind: "word", item: w })}>
                  <span className="gs-ar font-arabic" dir="rtl">{w.text}</span>
                  <span className="gs-def">{w.definition}</span>
                </button>
              ))}
            </div>
          ))}

          {data.words.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-head"><span>Words</span></div>
              {data.words.map((w) => {
                const m = w.match || {};
                const viaOther = m.field && !["text", "definition", "transliteration"].includes(m.field);
                return (
                  <button key={`w-${w.id}`} type="button" {...rowProps({ kind: "word", item: w })}>
                    <span className="gs-ar font-arabic" dir="rtl">{isArabic ? highlight(w.text, trimmed) : w.text}</span>
                    <span className="gs-def">
                      {w.transliteration && <span className="gs-translit">{m.field === "transliteration" ? highlight(w.transliteration, trimmed) : w.transliteration} · </span>}
                      {m.field === "definition" || !isArabic ? highlight(w.definition, trimmed) : w.definition}
                    </span>
                    {viaOther && m.snippet && (
                      <span className="gs-via">
                        {m.field === "example" ? "in example: " : m.field === "usage_notes" ? "in notes: " : `${m.field.replace("_", " ")}: `}
                        {isArabic ? <span className="font-arabic" dir="rtl">{highlight(m.snippet, trimmed)}</span> : highlight(m.snippet, trimmed)}
                      </span>
                    )}
                  </button>
                );
              })}
              <MoreButton group="words" />
            </div>
          )}

          {data.sentences.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-head"><span>Sentences</span></div>
              {data.sentences.map((s) => (
                <button key={`${s.kind}-${s.id}`} type="button" {...rowProps({ kind: "sentence", item: s })}>
                  <span className="gs-ar gs-sentence font-arabic" dir="rtl">{isArabic ? highlight(s.arabic, trimmed) : s.arabic}</span>
                  {s.translation && <span className="gs-def">{isArabic ? s.translation : highlight(s.translation, trimmed)}</span>}
                  <span className="gs-via">
                    {s.kind === "turn"
                      ? <>{s.topic?.name}{s.speaker ? ` · ${s.speaker}` : ""}</>
                      : <>Example of <span className="font-arabic" dir="rtl">{s.word?.text}</span></>}
                  </span>
                </button>
              ))}
              <MoreButton group="sentences" />
            </div>
          )}

          {data.topics.length > 0 && (
            <div className="gs-group">
              <div className="gs-group-head"><span>Topics</span></div>
              {data.topics.map((t) => (
                <button key={`t-${t.id}`} type="button" {...rowProps({ kind: "topic", item: t })}>
                  <span className="gs-topic">{highlight(t.name, trimmed)}</span>
                  {t.description && <span className="gs-def">{highlight(t.description, trimmed)}</span>}
                  <span className="gs-via">
                    {t.line_count} {t.line_count === 1 ? "line" : "lines"} · {t.word_count} {t.word_count === 1 ? "word" : "words"}
                    {t.visibility === "private" ? " · Private" : t.visibility === "public" ? " · Published" : t.visibility === "institute" ? " · Institute" : ""}
                    {t.is_owner ? " · Yours" : ""}
                  </span>
                </button>
              ))}
              <MoreButton group="topics" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
